import { buildFileFetchUrl, getPresignedFileUrl } from '../../../services/fileUrlService';
import { formatShareMoney, formatSignedShareMoney, sortShareBalances, isSettlementEntry, settlementReceiver } from './expenseShareUtils';

const csvEscape = (value) => {
  const text = String(value ?? '');
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const safeName = (name) => String(name || 'expenses').replace(/[^\w\-]+/g, '-').replace(/-+/g, '-').slice(0, 40);

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(blob);
});

const fileToDataUrl = async (fileKey) => {
  if (!fileKey) return '';
  if (String(fileKey).startsWith('data:') || String(fileKey).startsWith('blob:')) return String(fileKey);
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  try {
    const fetchUrl = buildFileFetchUrl(fileKey);
    if (fetchUrl) {
      const response = await fetch(fetchUrl, { headers });
      if (response.ok) {
        return blobToDataUrl(await response.blob());
      }
    }
  } catch {
    // fall through
  }
  try {
    const url = await getPresignedFileUrl(fileKey);
    if (!url) return '';
    const response = await fetch(url);
    if (!response.ok) return '';
    return blobToDataUrl(await response.blob());
  } catch {
    return '';
  }
};

const paidUsedFromEntries = (entries = [], people = []) => {
  const map = new Map();
  people.forEach((p) => {
    map.set(String(p.userId), { userId: p.userId, userName: p.userName, paid: 0, used: 0 });
  });
  entries.forEach((entry) => {
    const amt = Number(entry.amount) || 0;
    const payerId = String(entry.paidByUserId || entry.userId || '');
    if (payerId) {
      if (!map.has(payerId)) {
        map.set(payerId, {
          userId: payerId,
          userName: entry.paidByUserName || entry.userName || 'Someone',
          paid: 0,
          used: 0,
        });
      }
      map.get(payerId).paid += amt;
    }
    (entry.shares || []).forEach((share) => {
      const id = String(share.userId || '');
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, { userId: id, userName: share.userName || 'Someone', paid: 0, used: 0 });
      }
      map.get(id).used += Number(share.amount) || 0;
    });
  });
  return Array.from(map.values()).map((row) => ({
    ...row,
    net: Math.round((row.paid - row.used) * 100) / 100,
  }));
};

const statusLabel = (net) => {
  if (net > 0.004) return 'Owed';
  if (net < -0.004) return 'Owes';
  return 'Settled';
};

export const downloadExpenseShareCsv = ({
  eventName,
  currency = 'INR',
  entries = [],
  people = [],
}) => {
  const expenseEntries = entries.filter((entry) => !isSettlementEntry(entry));
  const settlements = entries.filter((entry) => isSettlementEntry(entry));
  const rows = paidUsedFromEntries(entries, people);
  const sorted = sortShareBalances(rows);
  const total = expenseEntries.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const lines = [
    ['Shared expenses'],
    ['Event', eventName || 'Event'],
    ['Currency', currency],
    ['Generated', new Date().toLocaleString()],
    ['Total spend', formatShareMoney(total, currency)],
    ['Entries', String(expenseEntries.length)],
    ['Settlements', String(settlements.length)],
    [],
    ['Balances'],
    ['Name', 'Paid', 'Used / share', 'Net', 'Status'],
    ...sorted.map((row) => [
      row.userName || 'Someone',
      formatShareMoney(row.paid, currency),
      formatShareMoney(row.used, currency),
      formatSignedShareMoney(row.net, currency),
      statusLabel(row.net),
    ]),
    [],
    ['Entry calculations'],
    ['Item', 'Paid by', 'Amount', 'Used by', 'Percent', 'Share amount'],
    ...expenseEntries.flatMap((entry) => {
      const shares = Array.isArray(entry.shares) && entry.shares.length
        ? entry.shares
        : [{ userName: '—', percent: '', amount: '' }];
      return shares.map((share) => [
        entry.item || 'Expense',
        entry.paidByUserName || entry.userName || 'Someone',
        formatShareMoney(entry.amount, currency),
        share.userName || 'Someone',
        share.percent != null && share.percent !== '' ? `${Number(share.percent).toFixed(2)}%` : '',
        formatShareMoney(share.amount, currency),
      ]);
    }),
    [],
    ['Settlements'],
    ['Paid by', 'Paid to', 'Amount', 'Note'],
    ...settlements.map((entry) => [
      entry.paidByUserName || entry.userName || 'Someone',
      settlementReceiver(entry)?.userName || 'Someone',
      formatShareMoney(entry.amount, currency),
      entry.item && entry.item !== 'Settlement' ? entry.item : '',
    ]),
  ];
  const csv = lines.map((row) => row.map(csvEscape).join(',')).join('\r\n');
  downloadBlob(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }), `${safeName(eventName)}-expenses.csv`);
};

export const downloadExpenseSharePdf = async ({
  eventName,
  currency = 'INR',
  entries = [],
  people = [],
}) => {
  const expenseEntries = entries.filter((entry) => !isSettlementEntry(entry));
  const settlements = entries.filter((entry) => isSettlementEntry(entry));
  const rows = sortShareBalances(paidUsedFromEntries(entries, people));
  const total = expenseEntries.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const images = {};
  await Promise.all(entries.map(async (entry) => {
    if (!entry?.imageUrl) return;
    images[entry.id || entry.imageUrl] = await fileToDataUrl(entry.imageUrl);
  }));

  const balanceRows = rows.map((row) => {
    const net = Number(row.net) || 0;
    const color = net > 0.004 ? '#059669' : net < -0.004 ? '#e11d48' : '#6b7280';
    return `<tr>
      <td>${escapeHtml(row.userName)}</td>
      <td>${escapeHtml(formatShareMoney(row.paid, currency))}</td>
      <td>${escapeHtml(formatShareMoney(row.used, currency))}</td>
      <td style="color:${color};font-weight:700">${escapeHtml(formatSignedShareMoney(net, currency))}</td>
      <td>${statusLabel(net)}</td>
    </tr>`;
  }).join('');

  const entryBlocks = expenseEntries.map((entry) => {
    const img = images[entry.id || entry.imageUrl];
    const shareRows = (entry.shares || []).map((share) => `<tr>
      <td>${escapeHtml(share.userName || 'Someone')}</td>
      <td>${share.percent != null ? `${Number(share.percent).toFixed(2)}%` : '—'}</td>
      <td>${escapeHtml(formatShareMoney(share.amount, currency))}</td>
    </tr>`).join('');
    return `<article class="entry">
      <h3>${escapeHtml(entry.item || 'Expense')}</h3>
      <p>Paid by <strong>${escapeHtml(entry.paidByUserName || entry.userName || 'Someone')}</strong>
        · ${escapeHtml(formatShareMoney(entry.amount, currency))}</p>
      ${shareRows ? `<table><thead><tr><th>Used by</th><th>%</th><th>Share</th></tr></thead><tbody>${shareRows}</tbody></table>` : ''}
      ${img ? `<img src="${img}" alt="${escapeHtml(entry.imageFileName || 'Receipt')}" />` : ''}
    </article>`;
  }).join('');

  const settlementBlocks = settlements.map((entry) => {
    const img = images[entry.id || entry.imageUrl];
    const receiver = settlementReceiver(entry);
    return `<article class="entry">
      <h3>Settlement</h3>
      <p><strong>${escapeHtml(entry.paidByUserName || entry.userName || 'Someone')}</strong>
        paid <strong>${escapeHtml(receiver?.userName || 'someone')}</strong>
        ${escapeHtml(formatShareMoney(entry.amount, currency))}</p>
      ${entry.item && entry.item !== 'Settlement' ? `<p>${escapeHtml(entry.item)}</p>` : ''}
      ${img ? `<img src="${img}" alt="${escapeHtml(entry.imageFileName || 'Payment')}" />` : ''}
    </article>`;
  }).join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(eventName || 'Shared expenses')}</title>
  <style>
    body { font-family: ui-sans-serif, system-ui, sans-serif; color: #111827; margin: 24px; }
    h1 { font-size: 22px; margin: 0 0 4px; }
    h2 { font-size: 16px; margin: 24px 0 8px; }
    h3 { font-size: 14px; margin: 0 0 6px; }
    p, td, th { font-size: 12px; }
    .meta { color: #6b7280; margin: 0 0 16px; }
    table { width: 100%; border-collapse: collapse; margin: 8px 0 12px; }
    th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
    th { background: #f9fafb; }
    .entry { page-break-inside: avoid; margin: 0 0 18px; padding: 12px 0; border-bottom: 1px solid #e5e7eb; }
    img { max-width: 320px; max-height: 240px; display: block; margin-top: 8px; border-radius: 8px; }
    @media print { body { margin: 12mm; } }
  </style>
</head>
<body>
  <h1>${escapeHtml(eventName || 'Shared expenses')}</h1>
  <p class="meta">Shared expenses · ${escapeHtml(new Date().toLocaleString())} · Total ${escapeHtml(formatShareMoney(total, currency))} · ${expenseEntries.length} entries</p>
  <h2>Balances</h2>
  <table>
    <thead><tr><th>Name</th><th>Paid</th><th>Used / share</th><th>Net</th><th>Status</th></tr></thead>
    <tbody>${balanceRows}</tbody>
  </table>
  <h2>Who spent</h2>
  ${entryBlocks || '<p>No entries.</p>'}
  <h2>Settlements</h2>
  ${settlementBlocks || '<p>No settlements recorded.</p>'}
</body>
</html>`;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument;
  doc.open();
  doc.write(html);
  doc.close();
  await new Promise((resolve) => {
    const imgs = Array.from(doc.images || []);
    if (!imgs.length) {
      resolve();
      return;
    }
    let left = imgs.length;
    const done = () => {
      left -= 1;
      if (left <= 0) resolve();
    };
    imgs.forEach((img) => {
      if (img.complete) done();
      else {
        img.onload = done;
        img.onerror = done;
      }
    });
  });
  iframe.contentWindow.focus();
  iframe.contentWindow.print();
  setTimeout(() => iframe.remove(), 1500);
};
